/**
 * Central transport switchboard — the dashboard's ONE declaration of which API
 * (REST vs the fast GraphQL read path) each data operation uses, plus the glue
 * that makes it user-tunable from Settings.
 *
 * The decision itself lives in `@authe/fe-commons/transport`'s `resolveTransport`
 * (precedence: per-op flag → master flag → registry default), read by every
 * adapter. That resolver reads a {@link FeatureFlags} set whose precedence is
 * `override > claim > default`. So this module owns the two dashboard-side layers:
 *
 *   - DEFAULT  — {@link transportFlagDefaults} feeds `FeatureFlags.defaults`, i.e.
 *     the product's per-operation choice (consolidating what used to be an inline
 *     literal in `app/providers`). Claims/overrides still win over it.
 *   - OVERRIDE — the Settings UI writes `graphql.<op>` overrides via
 *     `FeatureFlags.setOverride` (persisted in {@link FLAG_OVERRIDE_STORAGE_KEY}),
 *     the intended per-user toggle. Because every adapter already routes through
 *     `resolveTransport`, a change here takes effect everywhere with no rewiring.
 *
 * Granularity is per-OPERATION (the real toggle), grouped under resource-friendly
 * headers for the UI. The catalog kinds (entities/labels/tags/groups) share ONE
 * list operation (`catalog.list`), so they move together — shown as a single row.
 */
import type { FlagMap } from "@authe/fe-commons/flags";
import {
  GRAPHQL_MASTER_FLAG,
  flagForOperation,
  type DataOperation,
  type Transport,
} from "@authe/fe-commons/transport";

export type { DataOperation, Transport };
export { GRAPHQL_MASTER_FLAG, flagForOperation };

/**
 * localStorage key holding the `FlagMap` of local flag overrides. Matches
 * `FeatureFlags`' built-in default so existing overrides keep working; owned here
 * so the provider AND the Settings UI reference the SAME key (no magic strings).
 */
export const FLAG_OVERRIDE_STORAGE_KEY = "authe:flag-overrides";

/** Resource grouping for the Settings UI. */
export type TransportGroup = "transaction" | "address" | "catalog";

export interface TransportGroupMeta {
  id: TransportGroup;
  label: string;
}

/** Ordered groups (drives the Settings section layout). */
export const TRANSPORT_GROUPS: TransportGroupMeta[] = [
  { id: "transaction", label: "Transactions" },
  { id: "address", label: "Addresses" },
  { id: "catalog", label: "Catalog (entities, labels, tags & groups)" },
];

export interface TransportOperationConfig {
  op: DataOperation;
  group: TransportGroup;
  label: string;
  description: string;
  kind: "read" | "write";
  /** The dashboard's default transport for this operation. */
  default: Transport;
  /**
   * The GraphQL path is wired but has not been soaked in production.
   *
   * Surfaced in the UI rather than left implicit, because the REST fallback makes
   * an unsoaked path indistinguishable from a proven one from the outside: a read
   * that fails every time still renders, just slower. A reader choosing GraphQL
   * here deserves to know they are the one proving it.
   */
  experimental?: boolean;
}

/**
 * Every configurable operation and its product-default transport. Only
 * `catalog.list` defaults to GraphQL today (proven in prod); everything else
 * stays REST until promoted. Writes listed here are IDEMPOTENT only (attach/
 * detach, set/clear, upsert/delete) — GraphQL-first with a REST fallback, so a
 * retry converges; non-idempotent writes are never routed to GraphQL.
 *
 * An operation whose GraphQL path has not been proven carries `experimental` and
 * defaults to REST; promoting it is a matter of clearing the flag and flipping
 * the default, not of rewiring anything.
 */
export const TRANSPORT_CONFIG: TransportOperationConfig[] = [
  {
    op: "transactions.recentFeed",
    group: "transaction",
    label: "Recent feed (transaction table)",
    description:
      "First-paint recent-transactions feed behind the transaction table, wherever it appears. On REST the table loads from the plain list endpoint instead, with labels and entities resolved separately.",
    kind: "read",
    default: "rest",
  },
  {
    op: "transactions.header",
    group: "transaction",
    label: "Detail header",
    description: "First-paint header (chain, hash, finality, enrichment status) on the transaction detail page.",
    kind: "read",
    default: "rest",
  },
  {
    op: "address.list",
    group: "address",
    label: "Address list",
    description:
      "The paginated list behind every address table (tenant- and entity-scoped; the cross-chain lens stays on REST). The other address reads below enrich one address at a time — this is the read that fetches the rows. Not yet soaked in production, so it stays on REST by default and falls back to REST on error.",
    kind: "read",
    default: "rest",
    experimental: true,
  },
  {
    op: "address.overlays",
    group: "address",
    label: "Resolved overlays (incl. graph nodes)",
    description:
      "The effective label / tag / entity bundle for an address — on address pages, for a single node opened from a graph, and for the participants in a transaction's Addresses panel (one request for all of them). On REST the per-node lookup is skipped; a whole scene's or table's overlays always come from the REST batch, which is one call where GraphQL would be one per address.",
    kind: "read",
    default: "graphql",
  },
  {
    op: "address.detail",
    group: "address",
    label: "Detail row",
    description: "The merged address row + saved flag + tenancy scope switcher on the address detail page.",
    kind: "read",
    default: "graphql",
  },
  {
    op: "address.assignments.list",
    group: "address",
    label: "Assignments list",
    description: "Raw catalog assignments attached to an address.",
    kind: "read",
    default: "graphql",
  },
  {
    op: "address.entityMapping.read",
    group: "address",
    label: "Primary entity read",
    description: "The address's primary-entity pointer.",
    kind: "read",
    default: "graphql",
  },
  {
    op: "address.memberships.read",
    group: "address",
    label: "Entity membership list",
    description:
      "An entity's authoritative members (the company map on the entity page) — addresses claimed per chain, with their role. Distinct from the primary-entity pointer, which names one entity per address.",
    kind: "read",
    default: "graphql",
  },
  {
    op: "address.assignments.write",
    group: "address",
    label: "Assignment attach / detach",
    description: "Idempotent attach or detach of a catalog overlay.",
    kind: "write",
    default: "graphql",
  },
  {
    op: "address.primary.write",
    group: "address",
    label: "Primary entity set / clear",
    description: "Idempotent set or clear of the primary entity.",
    kind: "write",
    default: "graphql",
  },
  {
    op: "address.entityMapping.write",
    group: "address",
    label: "Entity mapping upsert / delete",
    description: "Idempotent write of the address ↔ entity mapping.",
    kind: "write",
    default: "graphql",
  },
  {
    op: "address.memberships.write",
    group: "address",
    label: "Entity membership batch write",
    description:
      "Mapping many addresses onto one entity in a single call. Idempotent per member, so a retry converges; a partial write reports which members landed.",
    kind: "write",
    default: "graphql",
  },
  {
    op: "catalog.list",
    group: "catalog",
    label: "Catalog lists",
    description: "List reads for entities, labels, tags & groups (one shared path).",
    kind: "read",
    default: "graphql",
  },
];

/** Whether GraphQL is the dashboard's default (master switch) — opt-in overall. */
export const TRANSPORT_MASTER_DEFAULT = true;

/**
 * The `FeatureFlags.defaults` map implied by {@link TRANSPORT_CONFIG}: the master
 * flag plus one `graphql.<op>` boolean per operation. This is the single source
 * of the dashboard's default transport (was an inline literal in `app/providers`).
 */
export function transportFlagDefaults(): FlagMap {
  const map: FlagMap = { [GRAPHQL_MASTER_FLAG]: TRANSPORT_MASTER_DEFAULT };
  for (const c of TRANSPORT_CONFIG) map[flagForOperation(c.op)] = c.default === "graphql";
  return map;
}

/** Read the local flag-override map straight from storage (for the Settings tri-state). */
export function readFlagOverrides(): FlagMap {
  try {
    if (typeof localStorage === "undefined") return {};
    return JSON.parse(localStorage.getItem(FLAG_OVERRIDE_STORAGE_KEY) ?? "{}") as FlagMap;
  } catch {
    return {};
  }
}

/**
 * The caller's explicit override for `op`, or `undefined` when none is set (the
 * "Default" state). Pure over an override map so it's testable and reused by the
 * UI without touching storage.
 */
export function transportOverrideFor(
  overrides: FlagMap,
  op: DataOperation,
): Transport | undefined {
  const value = overrides[flagForOperation(op)];
  if (value === undefined) return undefined;
  return value ? "graphql" : "rest";
}

/** The master-flag override (true/false), or `undefined` when unset. */
export function masterOverride(overrides: FlagMap): boolean | undefined {
  return overrides[GRAPHQL_MASTER_FLAG];
}
